import React, { useEffect, useState } from 'react';
import api from '../../api';
import { CoworkSessionRow, CoworkTask } from '../../types/cowork';
import { formatDuration } from '../../hooks/useCoworkAdmin';
import {
  Grid,
  Label,
  Muted,
  StatCard,
  StatValue,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Table,
  TableWrap,
} from '../ui';

interface History {
  sessions: CoworkSessionRow[];
  recentCompleted: CoworkTask[];
  totals: { viewerCompleted: number; streamerCompleted: number; sessionsCount: number };
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires',
  });
}

const CoworkHistoryTab: React.FC = () => {
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<History>('/cowork/history')
      .then(({ data }) => setHistory(data))
      .catch(() => setError('No se pudo cargar el historial'));
  }, []);

  if (error) return <Muted>{error}</Muted>;
  if (!history) return <Muted>Cargando historial…</Muted>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <Grid $cols={3}>
        <StatCard>
          <Label style={{ margin: 0 }}>Sesiones realizadas</Label>
          <StatValue style={{ fontSize: '1.5rem' }}>{history.totals.sessionsCount}</StatValue>
        </StatCard>
        <StatCard>
          <Label style={{ margin: 0 }}>Tareas de la comunidad</Label>
          <StatValue style={{ fontSize: '1.5rem' }}>{history.totals.viewerCompleted}</StatValue>
        </StatCard>
        <StatCard>
          <Label style={{ margin: 0 }}>Tareas de Euge</Label>
          <StatValue style={{ fontSize: '1.5rem' }}>{history.totals.streamerCompleted}</StatValue>
        </StatCard>
      </Grid>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Sesiones</SurfaceTitle>
        </SurfaceHeader>
        <SurfaceBody $flush>
          {history.sessions.length === 0 ? (
            <div style={{ padding: '1rem' }}><Muted>Todavía no hay sesiones registradas.</Muted></div>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Config</th>
                    <th>Ciclos</th>
                    <th>Focus</th>
                    <th>Participantes</th>
                    <th>Tareas completadas</th>
                  </tr>
                </thead>
                <tbody>
                  {history.sessions.map((session) => (
                    <tr key={session.id}>
                      <td style={{ color: '#fff' }}>{formatDate(session.startedAt)}</td>
                      <td>{session.endedAt ? formatDate(session.endedAt) : 'En curso'}</td>
                      <td>{session.focusMinutes}/{session.breakMinutes} min</td>
                      <td>{session.cyclesCompleted} de {session.totalCycles}</td>
                      <td>{formatDuration(session.focusMs)}</td>
                      <td>{session.participants}</td>
                      <td>{session.tasksCompleted} chat · {session.streamerTasksCompleted} Euge</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </SurfaceBody>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Últimas tareas completadas</SurfaceTitle>
        </SurfaceHeader>
        <SurfaceBody $flush>
          {history.recentCompleted.length === 0 ? (
            <div style={{ padding: '1rem' }}><Muted>Sin tareas completadas todavía.</Muted></div>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Quién</th>
                    <th>Tarea</th>
                    <th>Completada</th>
                  </tr>
                </thead>
                <tbody>
                  {history.recentCompleted.map((task) => (
                    <tr key={task.id}>
                      <td style={{ color: '#fff' }}>
                        {task.ownerType === 'streamer' ? 'Euge' : task.displayName || '—'}
                      </td>
                      <td style={{ color: '#fff' }}>{task.text}</td>
                      <td>{formatDate(task.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </SurfaceBody>
      </Surface>
    </div>
  );
};

export default CoworkHistoryTab;
